import { authorizedFetch, API_ORIGIN } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { INTERVIEWER_CANDIDATES_QUERY_KEY } from './use-interviewer-candidates'

export type ScorecardRecommendation = 'advance' | 'hold' | 'reject'

export interface KitLevel {
  ordinal: number
  label: string
  anchor: string
}

export interface KitTopic {
  topic: string
  competency_id: string | null
  target_level: number | null
  levels: KitLevel[]
}

export interface KitQuestion {
  id: string
  topic: string
  competency_id: string | null
  target_level: number | null
  question: string
  probes: string[]
  strong_answer: string
  weak_answer: string
  sort_order: number
}

export interface InterviewKit {
  id: string
  brief_id: string
  brief_title: string
  role_id: string | null
  role_title: string | null
  generated_at: string | null
  topics: KitTopic[]
  questions: KitQuestion[]
}

export interface ScorecardRating {
  topic: string
  competency_id: string | null
  level: number
  target_level: number | null
  notes: string
}

export interface Scorecard {
  id: string
  application_id: string
  profile_id: string
  interview_id: string | null
  interviewer_name: string | null
  recommendation: ScorecardRecommendation
  recommendation_label: string
  summary: string
  submitted_at: string
  ratings: ScorecardRating[]
}

/** Newest scorecard for a candidate on a brief, as the recruiter sees it. */
export interface ScorecardSummary {
  scorecard_id: string
  recommendation: ScorecardRecommendation
  recommendation_label: string
  interviewer_name: string | null
  submitted_at: string
  met_target: number
  rated: number
  scorecard_count: number
}

export interface ApplicationKit {
  application_id: string
  brief_id: string | null
  kit: InterviewKit | null
  scorecards: Scorecard[]
}

export interface SubmitScorecardData {
  recommendation: ScorecardRecommendation
  summary: string
  interview_id?: string | null
  ratings: ScorecardRating[]
}

const briefKitKey = (briefId: string) => ['interview-kit', 'brief', briefId] as const
const applicationKitKey = (applicationId: string) =>
  ['interview-kit', 'application', applicationId] as const

async function readJson<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.error || fallback)
  }
  return response.json() as Promise<T>
}

export function useBriefInterviewKit(briefId: string | null | undefined) {
  return useQuery({
    queryKey: briefKitKey(briefId ?? ''),
    enabled: Boolean(briefId),
    queryFn: async (): Promise<InterviewKit | null> =>
      readJson(
        await authorizedFetch(`${API_ORIGIN}/api/interview-kits/briefs/${briefId}/`),
        'Failed to load the interview kit',
      ),
  })
}

export function useApplicationInterviewKit(applicationId: string | null | undefined) {
  return useQuery({
    queryKey: applicationKitKey(applicationId ?? ''),
    enabled: Boolean(applicationId),
    queryFn: async (): Promise<ApplicationKit> =>
      readJson(
        await authorizedFetch(`${API_ORIGIN}/api/interview-kits/applications/${applicationId}/`),
        'Failed to load the interview kit',
      ),
  })
}

export function useGenerateInterviewKit() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (briefId: string): Promise<InterviewKit> =>
      readJson(
        await authorizedFetch(`${API_ORIGIN}/api/interview-kits/briefs/${briefId}/generate/`, {
          method: 'POST',
        }),
        'Failed to draft the interview kit',
      ),
    onSuccess: (kit) => {
      queryClient.setQueryData(briefKitKey(kit.brief_id), kit)
      queryClient.invalidateQueries({ queryKey: ['interview-kit', 'application'] })
    },
  })
}

export function useDeleteKitQuestion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (questionId: string): Promise<void> => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/interview-kits/questions/${questionId}/`,
        { method: 'DELETE' },
      )
      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.error || 'Failed to remove the question')
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['interview-kit'] }),
  })
}

export function useSubmitScorecard() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      applicationId,
      data,
    }: {
      applicationId: string
      data: SubmitScorecardData
    }): Promise<Scorecard> =>
      readJson(
        await authorizedFetch(
          `${API_ORIGIN}/api/interview-kits/applications/${applicationId}/scorecards/`,
          { method: 'POST', body: JSON.stringify(data) },
        ),
        'Failed to submit the scorecard',
      ),
    onSuccess: (_card, { applicationId }) => {
      queryClient.invalidateQueries({ queryKey: applicationKitKey(applicationId) })
      queryClient.invalidateQueries({ queryKey: INTERVIEWER_CANDIDATES_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['brief-candidates'] })
    },
  })
}
